import type { JevAction, JevMissingInput } from "./contracts";

export const jevCopy = {
  es: {
    title: "¿Qué querés hacer hoy?",
    subtitle:
      "Contanos qué necesitás y te llevamos al siguiente paso.",
    placeholder: "Quiero crear un CV para Product Manager…",
    rolePrompt: (role: string) => `¿Qué querés hacer para el puesto de ${role}?`,
    suggestionsTitle: "Probá estos prompts",
    suggestionLabels: {
      findJobs: "Buscar trabajos",
      createResume: "Crear un CV",
      prepareInterview: "Preparar una entrevista",
      matchJob: "Evaluar compatibilidad",
      adaptResume: "Adaptar mi CV",
      reviewResume: "Revisar CV para ATS",
    },
    findJobsPrompt: "Quiero buscar trabajos que coincidan con mi perfil…",
    editPromptHint: "Editá el pedido para preparar el siguiente paso.",
    selectRole: "Elegir puesto",
    findJobs: "Buscar trabajos",
    loadingJobs: "Cargando puestos…",
    processing: "Estamos preparando el siguiente paso…",
    error: "No pudimos procesar tu pedido. Volvé a intentar o describilo de otra manera.",
    retry: "Reintentar",
    clarify:
      "¿Qué necesitás hacer? Describí tu pedido con un poco más de detalle.",
    continue: "Continuar",
    upload: "Subir CV",
    loaded: "CV disponible",
    noResume: "Todavía no hay un CV cargado",
    role: "Puesto objetivo",
    location: "Ubicación de búsqueda",
    job: "Descripción del trabajo",
    jobHint:
      "Pegá la descripción completa (al menos 40 caracteres) o cargá un trabajo guardado. Una URL sola no alcanza.",
    context: "Completá los datos para continuar",
    storageError:
      "No pudimos transferir los datos. Habilitá el almacenamiento del navegador e intentá de nuevo.",
    importTitle: "Agregar tu CV",
    importDescription:
      "Subí tu CV o importá tu perfil para usarlo en este flujo.",
    disclaimer:
      "El análisis de match indica compatibilidad; no es una probabilidad de contratación.",
    actions: {
      "new-resume": "Crear CV",
      "adapt-resume": "Adaptar CV",
      "find-jobs": "Buscar trabajos",
      "cover-letter": "Carta de presentación",
      "match-job": "Evaluar match",
      "analyze-resume": "Revisar CV / ATS",
      "prepare-interview": "Preparar entrevista",
    } satisfies Record<JevAction, string>,
    missing: {
      resume: "Cargá tu CV",
      jobDescription: "Agregá la descripción del trabajo",
      targetRole: "Indicá el puesto",
      location: "Indicá la ubicación",
    } satisfies Record<JevMissingInput, string>,
  },
  en: {
    title: "What would you like to do?",
    subtitle:
      "Tell us what you need and we’ll guide you to the next step.",
    placeholder: "I want to create a resume for a Product Manager role…",
    rolePrompt: (role: string) => `What would you like to do for the ${role} role?`,
    suggestionsTitle: "Try these prompts",
    suggestionLabels: {
      findJobs: "Find jobs",
      createResume: "Create a resume",
      prepareInterview: "Prepare for an interview",
      matchJob: "Check my match",
      adaptResume: "Tailor my resume",
      reviewResume: "Review for ATS",
    },
    findJobsPrompt: "I want to find jobs that match my profile…",
    editPromptHint: "Edit the request to prepare the next step.",
    selectRole: "Select Role",
    findJobs: "Find jobs",
    loadingJobs: "Loading roles…",
    processing: "Getting the next step ready…",
    error: "We couldn't process your request. Try again or rephrase your request.",
    retry: "Try again",
    clarify: "What would you like to do? Add a little more detail to your request.",
    continue: "Continue",
    upload: "Upload resume",
    loaded: "Resume available",
    noResume: "No resume loaded yet",
    role: "Target role",
    location: "Search location",
    job: "Job description",
    jobHint:
      "Paste the complete description (at least 40 characters) or load a saved job. A URL alone is not enough.",
    context: "Complete the details to continue",
    storageError:
      "We couldn't transfer your details. Enable browser storage and try again.",
    importTitle: "Add your resume",
    importDescription:
      "Upload your resume or import your profile to use it in this workflow.",
    disclaimer:
      "The match analysis describes compatibility, not the probability of being hired.",
    actions: {
      "new-resume": "Create resume",
      "adapt-resume": "Tailor resume",
      "find-jobs": "Find jobs",
      "cover-letter": "Cover letter",
      "match-job": "Evaluate match",
      "analyze-resume": "Review resume / ATS",
      "prepare-interview": "Prepare interview",
    } satisfies Record<JevAction, string>,
    missing: {
      resume: "Upload your resume",
      jobDescription: "Add the job description",
      targetRole: "Enter the target role",
      location: "Enter the location",
    } satisfies Record<JevMissingInput, string>,
  },
};

export type JevPromptSuggestion = {
  id: string;
  label: string;
  prompt: string;
};

type PromptSuggestionContext = {
  hasResume: boolean;
  targetRole: string;
  location: string;
  selectedRole: string | null;
};

export function getJevPromptSuggestions(
  locale: "es" | "en",
  context: PromptSuggestionContext,
): JevPromptSuggestion[] {
  const copy = jevCopy[locale];
  const role = context.selectedRole ?? context.targetRole;
  const hasRole = role.trim().length > 0;
  const hasLocation = context.location.trim().length > 0;
  const findJobsPrompt =
    locale === "es"
      ? `Quiero encontrar trabajos${hasRole ? ` de ${role}` : ""}${hasLocation ? ` en ${context.location}` : ""} que coincidan con mi experiencia.`
      : `Find jobs${hasRole ? ` for ${role}` : ""}${hasLocation ? ` in ${context.location}` : ""} that match my experience.`;
  const createResumePrompt =
    locale === "es"
      ? `Quiero crear un CV profesional${hasRole ? ` para el puesto de ${role}` : " para mi próximo objetivo laboral"}, basado en mi experiencia real y sin inventar datos.`
      : `I want to create a professional resume${hasRole ? ` for a ${role} role` : " for my next career goal"}, based on my real experience without inventing details.`;
  const prepareInterviewPrompt =
    locale === "es"
      ? `Quiero prepararme para una entrevista${hasRole ? ` para el puesto de ${role}` : " para mi próximo puesto"}, con preguntas y respuestas de ejemplo.`
      : `Help me prepare for an interview${hasRole ? ` for a ${role} role` : " for my next role"}, with relevant questions and sample answers.`;
  if (!context.hasResume) {
    return [
      {
        id: "create-resume",
        label: copy.suggestionLabels.createResume,
        prompt: createResumePrompt,
      },
      {
        id: "find-jobs",
        label: copy.suggestionLabels.findJobs,
        prompt: findJobsPrompt,
      },
      {
        id: "prepare-interview",
        label: copy.suggestionLabels.prepareInterview,
        prompt: prepareInterviewPrompt,
      },
    ];
  }

  const jobReference =
    locale === "es"
      ? context.selectedRole
        ? `el puesto de ${role}`
        : "la descripción del trabajo que voy a agregar"
      : context.selectedRole
        ? `the ${role} role`
        : "the job description I add";

  return [
    {
      id: "find-jobs",
      label: copy.suggestionLabels.findJobs,
      prompt: findJobsPrompt,
    },
    {
      id: "match-job",
      label: copy.suggestionLabels.matchJob,
      prompt:
        locale === "es"
          ? `Quiero evaluar qué tan bien coincide mi CV con ${jobReference} y conocer las principales brechas.`
          : `Check how well my resume matches ${jobReference} and identify the main gaps.`,
    },
    {
      id: "adapt-resume",
      label: copy.suggestionLabels.adaptResume,
      prompt:
        locale === "es"
          ? `Quiero adaptar mi CV a ${jobReference}, manteniendo solo mi experiencia real.`
          : `Tailor my resume to ${jobReference}, using only my real experience.`,
    },
    {
      id: "analyze-resume",
      label: copy.suggestionLabels.reviewResume,
      prompt:
        locale === "es"
          ? "Quiero revisar mi CV para ATS y recibir mejoras concretas y priorizadas."
          : "Review my resume for ATS compatibility and suggest specific, prioritized improvements.",
    },
  ];
}
